/**
 * The setup step's own view of the revision it owns: how to record an edit, how
 * to commit it, and whether the revision itself forbids continuing.
 */

import { useCallback, useMemo } from 'react';
import {
  isSetupStepBlockedByRevision,
  resolveSetupCommitEvent,
  resolveSetupRevisionFailure,
  type SetupRevisionFailureCopy,
  type SetupRevisionStatus,
} from '../../../shared/lib/setupRevision';
import { useSetupRevisionStatus, useUIStore } from '../../../shared/stores/useUIStore';

export interface SetupRevisionStep {
  /** Records that the user changed a setup value inside the open revision. */
  markValueChanged: () => void;
  /** Moves an open revision into reconciliation; a no-op with nothing to commit. */
  beginReconciliation: () => void;
  /** Unlocks a revision stuck part-way so the step can apply the target again. */
  retryRecovery: () => void;
  /** Why the revision forbids continuing, or `null` while it does not. */
  blockedReason: string | null;
  /** Guidance for a revision stuck part-way, or `null` while it is not stuck. */
  recovery: SetupRevisionFailureCopy | null;
}

/** Wording for the one blocking revision state the user can act on themselves. */
const resolveBlockedReason = (status: SetupRevisionStatus): string | null => {
  if (!isSetupStepBlockedByRevision(status)) {
    return null;
  }

  return status === 'recoveryRequired'
    ? 'restore the previous connector parameter target, then retry the update'
    : 'wait for Revit to finish updating connector parameters';
};

export function useSetupRevisionStep(): SetupRevisionStep {
  const status = useSetupRevisionStatus();
  const applySetupRevisionEvent = useUIStore((state) => state.applySetupRevisionEvent);

  const markValueChanged = useCallback(() => {
    applySetupRevisionEvent('valueChanged');
  }, [applySetupRevisionEvent]);

  const beginReconciliation = useCallback(() => {
    const event = resolveSetupCommitEvent(status);
    if (!event) {
      return;
    }
    applySetupRevisionEvent(event);
  }, [applySetupRevisionEvent, status]);

  // No status read: the store ignores an event with no legal transition, and the
  // control offering this only exists while the revision is stuck.
  const retryRecovery = useCallback(() => {
    applySetupRevisionEvent('recoveryRetryAllowed');
  }, [applySetupRevisionEvent]);

  const recovery = useMemo(
    () =>
      status === 'recoveryRequired'
        ? resolveSetupRevisionFailure('applyFailedRecoveryRequired')
        : null,
    [status]
  );

  return {
    markValueChanged,
    beginReconciliation,
    retryRecovery,
    blockedReason: resolveBlockedReason(status),
    recovery,
  };
}
